/**
 * The cloud's status view (PLAN §10.2's status path): what the cloud knows
 * about the caller's tenant's sites and nodes — when it last heard from each
 * node's site link, and that link's state as the cloud sees it. The SPA's
 * connectivity panel reads it to fill the "Node→cloud" column when it cannot
 * reach a node itself (the "local nodes unreachable from here" case), and
 * `acp status` prints it.
 *
 * Cloud only (`routes.ts` spreads these into `ROUTES`; `@acp/handlers`'
 * rundown API excludes them, as it excludes the tenancy table). Every site of
 * the tenant — the automatic cloud site included — with its active nodes (a
 * revoked node is `GET /sites/{siteId}/nodes`' to show).
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import { z } from 'zod';
import type { RouteDef } from './routes.js';
import { witnessInForceSchema } from './witness.js';

/** A node's role: an on-prem site's `main`/`backup`/`witness` (D9, D21), or `cloud` for a cloud production's task (§9.1). */
export const nodeRoleSchema = z.enum(['main', 'backup', 'cloud', 'witness']);

/** The site link of one node, as the cloud sees it. */
export const cloudSideLinkStateSchema = z.enum(['connected', 'disconnected', 'never-connected']);

export const statusNodeSchema = z
  .object({
    nodeId: z.string().min(1).max(64),
    role: nodeRoleSchema,
    /** When the cloud last heard from this node's site link (ISO), or null if it never has. */
    lastSeen: z.iso.datetime({ offset: true }).nullable(),
    linkState: cloudSideLinkStateSchema,
    /** What operators call it (wave 4b-1). */
    label: z.string().max(128),
    /**
     * Its local HTTPS API (D16: `https://n-<id>.<tenant>.local.<stage domain>`), or null before it
     * has a name — what the SPA's connectivity manager watches and may fail over to (PLAN §10.2).
     */
    apiUrl: z.url().nullable(),
    /** Which witness is in force on the node and its expected failover time, from its last health report (wave 4c); null before one. */
    witness: witnessInForceSchema.nullable().optional(),
  })
  .strict();

export const statusSiteSchema = z
  .object({
    siteId: z.string().min(1).max(64),
    nodes: z.array(statusNodeSchema),
  })
  .strict();

export const statusResponseSchema = z.object({ sites: z.array(statusSiteSchema) }).strict();

export type StatusResponse = z.infer<typeof statusResponseSchema>;
export type StatusSite = z.infer<typeof statusSiteSchema>;
export type StatusNode = z.infer<typeof statusNodeSchema>;

export const STATUS_ROUTES = {
  getStatus: {
    id: 'getStatus',
    method: 'GET',
    path: '/status',
    tag: 'status',
    summary: 'The tenant’s sites and nodes, as the cloud sees them',
    description:
      'Every site of the caller’s tenant with its active nodes: role, when the cloud last heard from the node’s site link, and that link’s state (PLAN §10.2) — `connected` while its socket is open and it was heard from within three heartbeats. The cloud’s own liveness is the unauthenticated `GET /health`, outside this table.',
    params: null,
    query: null,
    body: null,
    response: statusResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
